/**
 * Standalone webhook HMAC validator — a "is this signature correct?"
 * tool independent of the live SSE timeline. Used by integrators who
 * captured a single delivery (logs, Postman, curl) and want to verify
 * it after the fact.
 *
 * Behaviour mirrors the Gateway → OP contract one-to-one:
 *   - Input `rawBody` MUST be the exact bytes Gateway POSTed. The UI
 *     never reformats it for the user — the textarea preserves
 *     whitespace, line endings, and ordering of keys (canonical JSON
 *     ordering is Gateway's responsibility, not ours).
 *   - `secret` is the value that was shared during `POST /subscriptions`.
 *   - `signature` is optional. When provided, we compare it via the
 *     same `timingSafeEqual` algorithm the bridge uses; when omitted,
 *     we just return the computed expected signature so the user can
 *     diff visually.
 *
 * All three fields are persisted to `localStorage` so a page reload
 * doesn't lose a 200-line JSON body the user just pasted.
 */
import { useEffect, useState } from 'react';
import type { HmacValidateResponse } from '@/lib/types';
import { validateWebhookHmac } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { loadString, saveString } from '@/lib/localStorage';

const STORAGE_KEY_BODY = 'op-emulator:hmac-validator:body';
const STORAGE_KEY_SECRET = 'op-emulator:hmac-validator:secret';
const STORAGE_KEY_SIG = 'op-emulator:hmac-validator:signature';

export function WebhookHmacValidator() {
  const [body, setBody] = useState(() => loadString(STORAGE_KEY_BODY, ''));
  const [secret, setSecret] = useState(() => loadString(STORAGE_KEY_SECRET, ''));
  const [signature, setSignature] = useState(() => loadString(STORAGE_KEY_SIG, ''));
  const [result, setResult] = useState<HmacValidateResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => saveString(STORAGE_KEY_BODY, body), [body]);
  useEffect(() => saveString(STORAGE_KEY_SECRET, secret), [secret]);
  useEffect(() => saveString(STORAGE_KEY_SIG, signature), [signature]);

  const onValidate = async () => {
    setErr(null);
    setBusy(true);
    try {
      const trimmedSig = signature.trim();
      const res = await validateWebhookHmac({
        rawBody: body,
        secret,
        ...(trimmedSig === '' ? {} : { signature: trimmedSig }),
      });
      setResult(res);
    } catch (e) {
      setErr((e as Error).message);
      setResult(null);
    } finally {
      setBusy(false);
    }
  };

  const clearAll = () => {
    setBody('');
    setSecret('');
    setSignature('');
    setResult(null);
    setErr(null);
  };

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor="hmac-validator-body">Raw body</Label>
        <Textarea
          id="hmac-validator-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder='{"event_id":"…","event_type":"tokensMinted",…}'
          rows={6}
          className="text-xs"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="hmac-validator-secret">Shared secret</Label>
        <Input
          id="hmac-validator-secret"
          type="password"
          value={secret}
          onChange={(e) => setSecret(e.target.value)}
          placeholder="hex / base64 / arbitrary string ≤512 chars"
          className="text-xs font-mono"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="hmac-validator-sig">Received signature (optional)</Label>
        <Input
          id="hmac-validator-sig"
          value={signature}
          onChange={(e) => setSignature(e.target.value)}
          placeholder="sha256=…"
          className="text-xs font-mono"
        />
      </div>
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="primary"
          onClick={() => void onValidate()}
          disabled={busy || body.length === 0 || secret.length === 0}
        >
          {busy ? 'Validating …' : 'Validate'}
        </Button>
        <Button size="sm" variant="ghost" onClick={clearAll}>
          Clear
        </Button>
      </div>
      {err !== null && (
        <div className="rounded-md bg-red-50 border border-red-200 p-2 text-xs text-red-900">
          {err}
        </div>
      )}
      {result !== null && <ValidatorResult result={result} />}
    </div>
  );
}

function ValidatorResult({ result }: { readonly result: HmacValidateResponse }) {
  return (
    <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3">
      <div className="flex items-center gap-2 flex-wrap">
        {result.errorKind !== undefined ? (
          <Badge variant="warning">{result.errorKind}</Badge>
        ) : result.hmacValid === true ? (
          <Badge variant="success">✓ HMAC valid</Badge>
        ) : result.hmacValid === false ? (
          <Badge variant="danger">✗ HMAC invalid</Badge>
        ) : (
          <Badge variant="muted">— no verdict</Badge>
        )}
      </div>
      {result.errorMessage !== undefined && (
        <div className="text-xs text-amber-900">{result.errorMessage}</div>
      )}
      {result.expectedSignature !== null && (
        <div className="grid grid-cols-[100px_1fr] gap-2 text-xs font-mono">
          <span className="text-slate-500">expected:</span>
          <span className="text-slate-800 break-all">{result.expectedSignature}</span>
        </div>
      )}
      {result.receivedSignature !== null && (
        <div className="grid grid-cols-[100px_1fr] gap-2 text-xs font-mono">
          <span className="text-slate-500">received:</span>
          <span
            className={
              result.hmacValid === false ? 'text-red-700 break-all' : 'text-slate-800 break-all'
            }
          >
            {result.receivedSignature}
          </span>
        </div>
      )}
    </div>
  );
}
